import { useMemo } from 'react';
import { FileText, Calculator, ChevronRight, Check } from 'lucide-react';

/**
 * Parses inline formatting: Bold (**text**), Code (`code`), and Citations ([DIN ...], [VdS ...], [Quelle: ...])
 */
export function parseInlineFormatting(text = '', onCitationClick) {
  if (!text || typeof text !== 'string') return text;

  // Tokenize regex for Citations, Bold, and Inline Code
  const tokenRegex = /(\[(?:Quelle:\s*[^\]]+|[^\]]+?\s+S\.\s*\d+|VdS\s*[\d]+|DIN\s*EN\s*[\d-]+|[A-Z0-9\s-]{4,}\s+S\.\s*\d+)\]|\*\*[^*]+\*\*|`[^`]+`)/g;
  const parts = [];
  let lastIdx = 0;
  let match;

  while ((match = tokenRegex.exec(text)) !== null) {
    const start = match.index;
    const end = tokenRegex.lastIndex;

    if (start > lastIdx) {
      parts.push(text.substring(lastIdx, start));
    }

    const token = match[1];

    if (token.startsWith('[') && token.endsWith(']')) {
      const citationText = token.slice(1, -1);
      parts.push(
        <button
          key={`cit-${start}`}
          type="button"
          data-testid="citation-pill"
          data-citation-ref={citationText}
          onClick={() => onCitationClick && onCitationClick(citationText)}
          className="inline-flex items-center gap-1 mx-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 border border-slate-300 text-slate-800 hover:border-[var(--primary,#e3000b)] hover:text-[var(--primary,#e3000b)] transition-colors cursor-pointer shadow-2xs align-baseline"
          title={`Nachweis anzeigen: ${citationText}`}
        >
          <FileText className="h-3 w-3 text-[var(--primary,#e3000b)] shrink-0" />
          <span className="truncate max-w-[200px]">{citationText}</span>
        </button>
      );
    } else if (token.startsWith('**') && token.endsWith('**')) {
      const boldText = token.slice(2, -2);
      parts.push(
        <strong key={`b-${start}`} className="font-semibold text-slate-900">
          {boldText}
        </strong>
      );
    } else if (token.startsWith('`') && token.endsWith('`')) {
      const codeText = token.slice(1, -1);
      parts.push(
        <code
          key={`c-${start}`}
          className="font-mono text-xs bg-slate-100 text-slate-800 px-1 py-0.5 rounded border border-slate-200"
        >
          {codeText}
        </code>
      );
    }

    lastIdx = end;
  }

  if (lastIdx < text.length) {
    parts.push(text.substring(lastIdx));
  }

  return parts.length > 0 ? parts : text;
}

/**
 * Renders full markdown-like structured message content including:
 * - Math formula blocks (\[ ... \])
 * - Headers (###, ##, #)
 * - Unordered bullet lists (- or *)
 * - Ordered numbered lists (1., 2.)
 * - Rich paragraphs with inline bold, code, and citations
 */
export function CopilotMessageRenderer({ content = '', onCitationClick }) {
  const blocks = useMemo(() => {
    if (!content || typeof content !== 'string') return [];

    // Split text into blocks by double newlines or formula blocks
    const lines = content.split('\n');
    const result = [];
    let currentList = null;
    let listType = null;

    const flushList = () => {
      if (currentList && currentList.length > 0) {
        result.push({
          type: listType,
          items: currentList,
        });
        currentList = null;
        listType = null;
      }
    };

    let i = 0;
    while (i < lines.length) {
      const rawLine = lines[i];
      const trimmed = rawLine.trim();

      // Skip empty lines
      if (!trimmed) {
        flushList();
        i++;
        continue;
      }

      // Check Math block: \[ ... \] or multi-line math block
      if (trimmed.startsWith('\\[') || trimmed.startsWith('$$')) {
        flushList();
        let mathContent = trimmed;
        if (!trimmed.endsWith('\\]') && !trimmed.endsWith('$$')) {
          i++;
          while (i < lines.length && !lines[i].trim().endsWith('\\]') && !lines[i].trim().endsWith('$$')) {
            mathContent += ' ' + lines[i].trim();
            i++;
          }
          if (i < lines.length) {
            mathContent += ' ' + lines[i].trim();
          }
        }
        const cleanMath = mathContent
          .replace(/^\\\[|^\$\$|\\\]$|\$\$$/g, '')
          .replace(/\\times/g, '×')
          .replace(/\\cdot/g, '·')
          .replace(/\\text\{([^}]+)\}/g, '$1')
          .replace(/\\,/g, ' ')
          .replace(/\\/g, '')
          .trim();

        result.push({
          type: 'math',
          content: cleanMath,
        });
        i++;
        continue;
      }

      // Check Header: ### or ## or #
      if (trimmed.startsWith('### ') || trimmed.startsWith('## ') || trimmed.startsWith('#### ')) {
        flushList();
        const headerText = trimmed.replace(/^#+\s*/, '');
        result.push({
          type: 'header',
          content: headerText,
        });
        i++;
        continue;
      }

      // Check Bullet list: - or *
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        if (listType && listType !== 'unordered') {
          flushList();
        }
        listType = 'unordered';
        if (!currentList) currentList = [];
        currentList.push(trimmed.replace(/^[-*]\s+/, ''));
        i++;
        continue;
      }

      // Check Numbered list: 1. or 2.
      const numberedMatch = trimmed.match(/^(\d+)\.\s+(.*)$/);
      if (numberedMatch) {
        if (listType && listType !== 'ordered') {
          flushList();
        }
        listType = 'ordered';
        if (!currentList) currentList = [];
        currentList.push(numberedMatch[2]);
        i++;
        continue;
      }

      // Regular paragraph line
      flushList();
      result.push({
        type: 'paragraph',
        content: trimmed,
      });
      i++;
    }

    flushList();
    return result;
  }, [content]);

  return (
    <div className="space-y-2.5 text-slate-800 text-xs leading-relaxed">
      {blocks.map((block, idx) => {
        if (block.type === 'header') {
          return (
            <h4
              key={`hdr-${idx}`}
              className="font-bold text-slate-900 text-sm mt-3 mb-1.5 flex items-center gap-1.5 border-b border-slate-200 pb-1"
            >
              <ChevronRight className="h-3.5 w-3.5 text-[var(--primary,#e3000b)] shrink-0" />
              {parseInlineFormatting(block.content, onCitationClick)}
            </h4>
          );
        }

        if (block.type === 'unordered') {
          return (
            <ul key={`ul-${idx}`} className="space-y-1.5 my-2 pl-1">
              {block.items.map((item, itemIdx) => (
                <li key={`li-${itemIdx}`} className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--primary,#e3000b)] shrink-0 mt-2" />
                  <span className="flex-1">{parseInlineFormatting(item, onCitationClick)}</span>
                </li>
              ))}
            </ul>
          );
        }

        if (block.type === 'ordered') {
          return (
            <ol key={`ol-${idx}`} className="space-y-1.5 my-2 pl-1">
              {block.items.map((item, itemIdx) => (
                <li key={`oli-${itemIdx}`} className="flex items-start gap-2">
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-slate-200 text-slate-700 text-xs font-bold shrink-0 mt-0.5">
                    {itemIdx + 1}
                  </span>
                  <span className="flex-1">{parseInlineFormatting(item, onCitationClick)}</span>
                </li>
              ))}
            </ol>
          );
        }

        if (block.type === 'math') {
          return (
            <div
              key={`math-${idx}`}
              className="my-3 rounded-lg border border-blue-200 bg-blue-50/70 p-3 text-blue-950 font-mono text-xs flex items-center gap-2.5 shadow-2xs"
            >
              <Calculator className="h-4 w-4 text-blue-600 shrink-0" />
              <div className="flex-1 overflow-x-auto font-semibold tracking-wide">
                {block.content}
              </div>
            </div>
          );
        }

        return (
          <p key={`p-${idx}`} className="text-slate-700 leading-relaxed">
            {parseInlineFormatting(block.content, onCitationClick)}
          </p>
        );
      })}
    </div>
  );
}
